import { format, parse } from 'date-fns'
import { isDefined } from '../../misc/functions.utilities'
import { DrugGS1DatamatrixContent } from '../../model/Drug'

const splitAtSeparator = (
  content: string,
  separator: string,
): { content: string; leftOver: string } => {
  const separatorIndex = content.indexOf(separator)

  if (separatorIndex === -1) {
    return { content, leftOver: '' }
  } else {
    return {
      content: content.substring(0, separatorIndex),
      leftOver: content.substring(separatorIndex + 1),
    }
  }
}

export const getContentFromDrugGs1Code = (
  gs1Code: string,
  datamatrixContent: DrugGS1DatamatrixContent = {},
) => {
  const FNC1_CHAR = '\u001D' // FNC1 character used as a separator in GS1 codes

  if (gs1Code.startsWith(FNC1_CHAR)) {
    gs1Code = gs1Code.substring(1)
  }

  const indication = gs1Code.substring(0, 2)
  const truncatedContent = gs1Code.substring(2)

  const extracted: Partial<DrugGS1DatamatrixContent> = {}
  let leftOver = ''

  switch (indication) {
    case '01': {
      // CIP 13
      const cip = truncatedContent.substring(0, 14)

      leftOver = truncatedContent.substring(14)
      extracted.cip = cip

      break
    }
    case '17': {
      // Expiration date
      const expirationRaw = truncatedContent.substring(0, 6)
      const expirationParsed = parse(expirationRaw, 'yyMMdd', new Date())

      leftOver = truncatedContent.substring(6)
      extracted.expirationDate = expirationParsed

      break
    }
    case '11': {
      // Manufacturing date
      const manufacturingRaw = truncatedContent.substring(0, 6)
      const manufacturingParsed = parse(manufacturingRaw, 'yyMMdd', new Date())

      leftOver = truncatedContent.substring(6)
      extracted.manufacturingDate = manufacturingParsed

      break
    }
    case '10': {
      // Lot number
      const { content, leftOver: newLeftOver } = splitAtSeparator(truncatedContent, FNC1_CHAR)
      extracted.lotNumber = content
      leftOver = newLeftOver

      break
    }
    case '21': {
      // Serial number (variable length, max 20, ends with FNC1 or end of string)
      const { content, leftOver: newLeftOver } = splitAtSeparator(truncatedContent, FNC1_CHAR)
      extracted.serialNumber = content
      leftOver = newLeftOver

      break
    }
    default:
  }

  const merged = { ...datamatrixContent, ...extracted }
  if (leftOver) {
    return getContentFromDrugGs1Code(leftOver, merged)
  } else {
    return merged
  }
}

export const formatDatamatrixContent = (datamatrixContent: DrugGS1DatamatrixContent) => {
  const { cip, expirationDate, lotNumber, manufacturingDate, drugName, serialNumber } =
    datamatrixContent

  const cipLabel = cip ? `Code CIP : ${cip}` : undefined
  const lotNumberLabel = lotNumber ? `Numéro de lot : ${lotNumber}` : undefined
  const serialNumberLabel = serialNumber ? `Numéro de série : ${serialNumber}` : undefined
  const expirationLabel = expirationDate
    ? `Date d'expiration : ${format(expirationDate, 'dd-MM-yyyy')}`
    : undefined
  const manufacturingLabel = manufacturingDate
    ? `Date de fabrication : ${format(manufacturingDate, 'dd-MM-yyyy')}`
    : undefined

  const fragments = [
    drugName,
    cipLabel,
    expirationLabel,
    manufacturingLabel,
    lotNumberLabel,
    serialNumberLabel,
  ]
  return fragments.filter(isDefined).join(' / ')
}
